/**
 * Wire schemas for module 5 (equipment and inventory): the site store (stock
 * items, the append-only movement ledger, counts), material requests,
 * material submittals, materials on site, and plant (the register, usage
 * logs, pre-use checks, defects and requests).
 *
 * Synced to the device: stock items, stock movements, material requests,
 * equipment, equipment logs and equipment checks. The rest is online.
 *
 * What a reader receives depends on its rights (the server strips it): store
 * balances need `inventory.view`, unit costs, stock value and hire rates need
 * `cost.view_internal`. Optional fields default so a newer server never breaks
 * an older client. Quantities arrive as decimal strings and stay strings.
 */
import { z } from 'zod';

const id = z.string();
const nullableId = z.string().nullable().default(null);
const date = z.string().nullable().default(null);
const text = z.string().default('');
const version = z.number().int().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);
const qty = z.union([z.string(), z.number()]).transform(String);
const optionalQty = z
  .union([z.string(), z.number()])
  .transform(String)
  .nullable()
  .optional()
  .catch(undefined);
const money = z
  .object({ amount_minor: z.number().int(), currency: z.string() })
  .nullable()
  .optional()
  .catch(undefined);
export type MoneyValue = NonNullable<z.infer<typeof money>>;

const party = z
  .object({ id: z.string(), name: z.string().default(''), role: z.string().default('') })
  .nullable()
  .catch(null)
  .default(null);

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

export const MOVEMENT_KINDS = [
  'receipt',
  'issue',
  'return',
  'transfer',
  'wastage',
  'adjustment',
] as const;
export type MovementKind = (typeof MOVEMENT_KINDS)[number];

export const WASTAGE_REASONS = ['waste', 'damage', 'theft', 'expired', 'other'] as const;
export type WastageReason = (typeof WASTAGE_REASONS)[number];

export const REQUEST_STATUSES = [
  'requested',
  'partially_issued',
  'issued',
  'rejected',
  'cancelled',
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const SUBMITTAL_STATUSES = [
  'draft',
  // Module 6: a subcontractor's design submittal, with the main contractor.
  'contractor_review',
  'submitted',
  'approved',
  'approved_as_noted',
  'rejected',
  'resubmit',
] as const;
export type SubmittalStatus = (typeof SUBMITTAL_STATUSES)[number];
export const SUBMITTAL_DECISIONS = [
  'approved',
  'approved_as_noted',
  'resubmit',
  'rejected',
] as const;
export type SubmittalDecision = (typeof SUBMITTAL_DECISIONS)[number];

export const EQUIPMENT_CATEGORIES = [
  'excavator',
  'loader',
  'crane',
  'hoist',
  'concrete_mixer',
  'vibrator',
  'compactor',
  'generator',
  'pump',
  'scaffold',
  'vehicle',
  'small_tool',
  'other',
] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

export const EQUIPMENT_STATUSES = ['available', 'in_use', 'under_repair', 'off_hire'] as const;
export type EquipmentStatus = (typeof EQUIPMENT_STATUSES)[number];
export const HIRE_PERIODS = ['hour', 'day', 'week', 'month'] as const;
export type HirePeriod = (typeof HIRE_PERIODS)[number];
export const FUEL_LEVELS = ['full', '3/4', '1/2', '1/4', 'empty'] as const;
export const CHECK_RESULTS = ['pass', 'fail', 'na'] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];

// ---------------------------------------------------------------------------
// Synced: the store
// ---------------------------------------------------------------------------

export const stockItemSchema = z.object({
  id,
  project_id: id,
  name: z.string(),
  unit: z.string(),
  category: text,
  product_id: nullableId,
  boq_lineage: nullableId,
  submittal_id: nullableId,
  default_location_id: nullableId,
  notes: text,
  // inventory.view only
  reorder_level: optionalQty,
  on_hand: optionalQty,
  balances: z.record(z.string(), z.string()).optional().catch(undefined),
  received_total: optionalQty,
  issued_total: optionalQty,
  wasted_total: optionalQty,
  open_discrepancies: z.number().int().optional().catch(undefined),
  low_stock: z.boolean().optional().catch(undefined),
  // cost.view_internal only
  avg_cost: money,
  value: money,
  version,
});
export type StockItem = z.infer<typeof stockItemSchema>;
export const STOCK_ITEM_EDITABLE = [
  'name',
  'unit',
  'category',
  'reorder_level',
  'default_location_id',
  'submittal_id',
  'notes',
] as const;

export const stockMovementSchema = z.object({
  id,
  project_id: id,
  reference: text,
  item_id: id,
  kind: z.enum(MOVEMENT_KINDS).catch('issue'),
  quantity: qty,
  delta: qty,
  location_id: nullableId,
  to_location_id: nullableId,
  crew_id: nullableId,
  task_id: nullableId,
  work_zone_id: nullableId,
  to_party_id: nullableId,
  received_by_name: text,
  material_request_id: nullableId,
  request_line_id: text,
  reason: text,
  note: text,
  delivery_id: nullableId,
  stock_count_id: nullableId,
  occurred_at: z.string(),
  recorded_by_id: nullableId,
  warnings: z.array(z.string()).catch([]).default([]),
  discrepancy: z.boolean().default(false),
  discrepancy_note: text,
  review_status: text,
  reviewed_at: date,
  review_note: text,
  unit_cost: money,
  version,
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

export const requestLineSchema = z.object({
  id: z.string(),
  item_id: nullableId,
  description: z.string(),
  unit: z.string(),
  quantity: qty,
  issued: z.union([z.string(), z.number()]).transform(String).catch('0').default('0'),
});
export type RequestLine = z.infer<typeof requestLineSchema>;

export const materialRequestSchema = z.object({
  id,
  project_id: id,
  reference: text,
  requested_by_id: nullableId,
  requested_by: text,
  requester_party_id: z.string().default(''),
  needed_by: date,
  task_id: nullableId,
  zone_id: nullableId,
  crew_id: nullableId,
  notes: text,
  lines: z.array(requestLineSchema).catch([]).default([]),
  status: z.enum(REQUEST_STATUSES).catch('requested'),
  decided_at: date,
  decision_note: text,
  requisition_id: nullableId,
  created_at: date,
  version,
});
export type MaterialRequest = z.infer<typeof materialRequestSchema>;
export const REQUEST_EDITABLE = [
  'needed_by',
  'task_id',
  'zone_id',
  'crew_id',
  'notes',
  'lines',
] as const;

// ---------------------------------------------------------------------------
// Synced: plant
// ---------------------------------------------------------------------------

export const checklistItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  critical: z.boolean().default(false),
});
export type EquipmentChecklistItem = z.infer<typeof checklistItemSchema>;

const hireCostSchema = z
  .object({
    days: z.number().int(),
    from: date,
    to: date,
    amount: z.object({ amount_minor: z.number().int(), currency: z.string() }),
    hours: z.string().optional(),
  })
  .nullable()
  .optional()
  .catch(undefined);

export const equipmentSchema = z.object({
  id,
  project_id: id,
  reference: text,
  name: z.string(),
  category: z.enum(EQUIPMENT_CATEGORIES).catch('other'),
  ownership: z.enum(['owned', 'hired']).catch('owned'),
  owner_party_id: z.string().default(''),
  supplier_org_id: nullableId,
  supplier_name: text,
  serial_number: text,
  plate_number: text,
  hire_period: z.enum(HIRE_PERIODS).catch('day'),
  on_hire_date: date,
  off_hire_date: date,
  status: z.enum(EQUIPMENT_STATUSES).catch('available'),
  current_zone_id: nullableId,
  operator_id: nullableId,
  operator_name: text,
  next_service_date: date,
  meter_unit: z.string().default('hours'),
  last_meter_reading: z.string().nullable().default(null),
  notes: text,
  checklist: z.array(checklistItemSchema).catch([]).default([]),
  // cost.view_internal only
  hire_rate: money,
  hire_cost: hireCostSchema,
  version,
});
export type Equipment = z.infer<typeof equipmentSchema>;

export const equipmentLogSchema = z.object({
  id,
  project_id: id,
  equipment_id: id,
  log_date: z.string(),
  hours_worked: qty.catch('0').default('0'),
  idle_hours: qty.catch('0').default('0'),
  breakdown_hours: qty.catch('0').default('0'),
  meter_reading: z.string().nullable().default(null),
  fuel_litres: z.string().nullable().default(null),
  task_id: nullableId,
  zone_id: nullableId,
  operator_id: nullableId,
  notes: text,
  recorded_by_id: nullableId,
  verified_at: date,
  created_at: date,
  version,
});
export type EquipmentLog = z.infer<typeof equipmentLogSchema>;
export const LOG_EDITABLE = [
  'hours_worked',
  'idle_hours',
  'breakdown_hours',
  'meter_reading',
  'fuel_litres',
  'task_id',
  'zone_id',
  'operator_id',
  'notes',
] as const;

export const checkAnswerSchema = z.object({
  key: z.string(),
  label: z.string().default(''),
  critical: z.boolean().default(false),
  result: z.enum(CHECK_RESULTS).catch('pass'),
  note: text,
});
export type CheckAnswer = z.infer<typeof checkAnswerSchema>;

export const equipmentCheckSchema = z.object({
  id,
  project_id: id,
  equipment_id: id,
  check_date: date,
  checked_at: z.string(),
  operator_id: nullableId,
  checked_by_id: nullableId,
  items: z.array(checkAnswerSchema).catch([]).default([]),
  result: z.enum(['pass', 'fail']).catch('pass'),
  critical_failure: z.boolean().default(false),
  meter_reading: z.string().nullable().default(null),
  fuel_level: text,
  notes: text,
  version,
});
export type EquipmentCheck = z.infer<typeof equipmentCheckSchema>;

// ---------------------------------------------------------------------------
// Online
// ---------------------------------------------------------------------------

export const storeSummarySchema = z.object({
  items: count,
  low_stock: count,
  out_of_stock: count,
  open_discrepancies: count,
  issues_this_week: count,
  open_requests: count,
  stock_value: money,
});
export type StoreSummary = z.infer<typeof storeSummarySchema>;

export const countLineSchema = z.object({
  id: z.string(),
  item_id: z.string(),
  expected: z.string().nullable().default(null),
  counted: z.string().nullable().default(null),
  variance: z.string().nullable().default(null),
  note: text,
});
export type CountLine = z.infer<typeof countLineSchema>;

export const stockCountSchema = z.object({
  id,
  project_id: id,
  reference: text,
  location_id: nullableId,
  status: z.enum(['open', 'submitted', 'approved', 'rejected', 'cancelled']).catch('open'),
  lines: z.array(countLineSchema).catch([]).default([]),
  notes: text,
  counted_by_id: nullableId,
  counted_by: text,
  created_by_id: nullableId,
  submitted_at: date,
  decided_by: text,
  decided_at: date,
  decision_note: text,
  created_at: date,
  version,
});
export type StockCount = z.infer<typeof stockCountSchema>;

export const stockItemDetailSchema = stockItemSchema.extend({
  movements: z.array(stockMovementSchema).catch([]).default([]),
});
export type StockItemDetail = z.infer<typeof stockItemDetailSchema>;

export const materialRequestDetailSchema = materialRequestSchema.extend({
  issues: z.array(stockMovementSchema).catch([]).default([]),
});
export type MaterialRequestDetail = z.infer<typeof materialRequestDetailSchema>;

const historySchema = z.object({
  id: z.string(),
  revision: z.number().int(),
  status: z.enum(SUBMITTAL_STATUSES).catch('draft'),
  code: text,
  submitted_at: date,
  reviewed_at: date,
  reviewed_by: text,
  review_comments: text,
});

export const SUBMITTAL_KINDS = [
  'material',
  'shop_drawing',
  'method_statement',
  'technical_approval',
  'om_manual',
  'as_built',
] as const;
export type SubmittalKind = (typeof SUBMITTAL_KINDS)[number];

export const submittalSchema = z.object({
  id,
  project_id: id,
  reference: text,
  /** Module 6: materials and design submittals share one workflow. */
  kind: z.enum(SUBMITTAL_KINDS).catch('material').default('material'),
  /** A approved · B approved as noted · C revise and resubmit · D rejected. */
  code: text,
  number: count,
  revision: count,
  previous_id: nullableId,
  title: z.string(),
  description: text,
  product_id: nullableId,
  supplier_org_id: nullableId,
  supplier_name: text,
  manufacturer: text,
  spec_reference: text,
  location: text,
  quantity_text: text,
  sample_provided: z.boolean().default(false),
  sample_reference: text,
  document_id: nullableId,
  media_asset_ids: z.array(z.string()).catch([]).default([]),
  notes: text,
  status: z.enum(SUBMITTAL_STATUSES).catch('draft'),
  submitted_by_id: nullableId,
  submitted_by: text,
  submitted_by_party: party,
  reviewer_party: party,
  submitted_at: date,
  reviewed_by: text,
  reviewed_by_party: party,
  reviewed_at: date,
  review_comments: text,
  spec_section_id: nullableId,
  spec_section: z
    .object({ id: z.string(), number: z.string(), title: z.string() })
    .nullable()
    .catch(null)
    .default(null),
  due_date: date,
  forwarded_by: text,
  forwarded_at: date,
  contractor_comments: text,
  created_by_id: nullableId,
  created_at: date,
  history: z.array(historySchema).optional().catch(undefined),
  version,
});
export type Submittal = z.infer<typeof submittalSchema>;

export const materialsOnSiteRowSchema = z.object({
  item_id: z.string(),
  name: z.string(),
  unit: z.string(),
  category: text,
  received: z.string(),
  issued: z.string(),
  wasted: z.string(),
  on_hand: z.string(),
  unit_cost: money,
  value: money,
  unpriced: z.boolean().default(false),
});
export type MaterialsOnSiteRow = z.infer<typeof materialsOnSiteRowSchema>;

export const materialsOnSiteSchema = z.object({
  as_of: z.string(),
  currency: z.string().default('KES'),
  rows: z.array(materialsOnSiteRowSchema).catch([]).default([]),
  total: z.object({ amount_minor: z.number().int(), currency: z.string() }),
  unpriced_items: count,
});
export type MaterialsOnSite = z.infer<typeof materialsOnSiteSchema>;

export const defectSchema = z.object({
  id,
  project_id: id,
  reference: text,
  equipment_id: id,
  check_id: nullableId,
  description: z.string(),
  severity: z.enum(['critical', 'major', 'minor']).catch('major'),
  status: z.enum(['open', 'in_repair', 'fixed']).catch('open'),
  reported_by: text,
  reported_at: date,
  repair_started_at: date,
  fixed_at: date,
  repair_note: text,
  version,
});
export type Defect = z.infer<typeof defectSchema>;

const clashSchema = z.object({
  request_id: z.string(),
  reference: z.string(),
  from: z.string(),
  to: z.string(),
  description: text,
});
export type Clash = z.infer<typeof clashSchema>;

export const equipmentDetailSchema = equipmentSchema.extend({
  defects: z.array(defectSchema).catch([]).default([]),
  logs: z.array(equipmentLogSchema).catch([]).default([]),
  checks: z.array(equipmentCheckSchema).catch([]).default([]),
  allocations: z.array(clashSchema).catch([]).default([]),
});
export type EquipmentDetail = z.infer<typeof equipmentDetailSchema>;

export const equipmentRequestSchema = z.object({
  id,
  project_id: id,
  reference: text,
  category: z.enum(EQUIPMENT_CATEGORIES).catch('other'),
  description: z.string(),
  quantity: count,
  needed_from: z.string(),
  needed_to: z.string(),
  task_id: nullableId,
  zone_id: nullableId,
  notes: text,
  requested_by_id: nullableId,
  requested_by: text,
  requester_party_id: z.string().default(''),
  status: z.enum(['requested', 'approved', 'rejected', 'cancelled']).catch('requested'),
  fulfilment: z.enum(['', 'allocate', 'hire']).catch(''),
  equipment_id: nullableId,
  rfq_id: nullableId,
  decided_at: date,
  decision_note: text,
  allocation: z
    .object({ clashes: z.array(clashSchema).catch([]).default([]) })
    .partial()
    .catch({})
    .default({}),
  clashes: z.array(clashSchema).optional().catch(undefined),
  created_at: date,
  version,
});
export type EquipmentRequest = z.infer<typeof equipmentRequestSchema>;

const utilisationRowSchema = z.object({
  equipment_id: z.string(),
  reference: z.string(),
  name: z.string(),
  category: z.string(),
  ownership: z.string(),
  status: z.string(),
  days_on_site: count,
  available_hours: z.string(),
  hours_used: z.string(),
  idle_hours: z.string(),
  breakdown_hours: z.string(),
  fuel_litres: z.string(),
  utilisation_percent: z.string().nullable().default(null),
  defects: count,
});
export type UtilisationRow = z.infer<typeof utilisationRowSchema>;

export const utilisationSchema = z.object({
  from: z.string(),
  to: z.string(),
  working_day_hours: z.string(),
  rows: z.array(utilisationRowSchema).catch([]).default([]),
  totals: z.object({
    available_hours: z.string(),
    hours_used: z.string(),
    idle_hours: z.string(),
    breakdown_hours: z.string(),
    utilisation_percent: z.string().nullable().default(null),
  }),
});
export type Utilisation = z.infer<typeof utilisationSchema>;
